<template>
  <div class="show_all_content_wrapper delegates_live_page">
    <main>
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("PLACEHOLDERS.delegates_live_management") }}</h5>
          <span class="subtitle">
            {{ $t("PLACEHOLDERS.active_delegates") }}: {{ delegates.length }}
            |
            {{ $t("PLACEHOLDERS.distribution_points") }}:
            {{ distributionPoints.length }}
          </span>
        </div>

        <delegates-map-legend />
      </div>

      <div class="map_container">
        <delegates-map
          ref="delegatesMap"
          :delegates="delegates"
          :distribution-points="distributionPoints"
          :center="mapCenter"
          :zoom="mapZoom"
          :map-options="mapOptions"
          :tracking-delegate="trackingDelegate"
          :default-avatar="defaultAvatar"
          :show-sidebar="showSidebar"
          @delegate-click="selectDelegate"
          @distribution-group-click="onDistributionGroupClick"
          @stop-tracking="stopTracking"
          @open-delegate="navigateToDelegate"
        />

        <transition name="slide">
          <aside v-if="showSidebar" class="details_sidebar">
            <div class="sidebar_header">
              <h4>
                {{
                  selectedType === "delegate"
                    ? $t("PLACEHOLDERS.delegate_details")
                    : $t("PLACEHOLDERS.distribution_point_details")
                }}
              </h4>
              <button class="close_btn" @click="closeSidebar">
                <i class="fas fa-times"></i>
              </button>
            </div>

            <delegate-details-panel
              v-if="selectedType === 'delegate'"
              :delegate="selectedDelegate"
              :default-avatar="defaultAvatar"
              :is-tracking="isTrackingSelectedDelegate"
              @track="startTracking"
              @open-bundle="navigateToBundle"
              @open-delegate="navigateToDelegate"
            />

            <distribution-point-details-panel
              v-if="selectedType === 'distribution_point'"
              :point="selectedDistributionPoint"
            />
          </aside>
        </transition>
      </div>

      <div v-if="isLoading" class="loading_overlay">
        <div class="loader"></div>
        <span>{{ $t("PLACEHOLDERS.loading") }}...</span>
      </div>
    </main>

    <distribution-point-picker-dialog
      v-model="showPointPicker"
      :points="overlappingPoints"
      @select="onPickDistributionPoint"
    />
  </div>
</template>

<script>
import io from "socket.io-client";
import { DEFAULT_AVATAR_URL, SOCKET_URL } from "@/config/app";
import DelegatesMapLegend from "./components/DelegatesMapLegend.vue";
import DelegatesMap from "./components/DelegatesMap.vue";
import DelegateDetailsPanel from "./components/DelegateDetailsPanel.vue";
import DistributionPointDetailsPanel from "./components/DistributionPointDetailsPanel.vue";
import DistributionPointPickerDialog from "./components/DistributionPointPickerDialog.vue";
import {
  filterActiveDistributionPoints,
  fitMapToMarkers,
  mapActiveDeliveringToDelegate,
  parseCoordinate,
} from "./utils/delegatesMapHelpers";

const MAP_OPTIONS = {
  styles: [
    {
      featureType: "poi",
      elementType: "labels",
      stylers: [{ visibility: "off" }],
    },
  ],
};

export default {
  name: "ShowAllDelegates",

  components: {
    DelegatesMapLegend,
    DelegatesMap,
    DelegateDetailsPanel,
    DistributionPointDetailsPanel,
    DistributionPointPickerDialog,
  },

  data() {
    return {
      isLoading: true,
      delegates: [],
      distributionPoints: [],
      socket: null,
      mapCenter: { lat: 24.7136, lng: 46.6753 },
      mapZoom: 10,
      mapOptions: MAP_OPTIONS,
      showSidebar: false,
      selectedType: null,
      selectedDelegate: null,
      selectedDistributionPoint: null,
      trackingDelegate: null,
      defaultAvatar: DEFAULT_AVATAR_URL,
      showPointPicker: false,
      overlappingPoints: [],
    };
  },

  computed: {
    isTrackingSelectedDelegate() {
      return (
        !!this.trackingDelegate &&
        !!this.selectedDelegate &&
        this.trackingDelegate.id === this.selectedDelegate.id
      );
    },
  },

  methods: {
    getMapInstance() {
      return this.$refs.delegatesMap?.getMapInstance();
    },

    async fetchDistributionPoints() {
      try {
        const response = await this.$axios.get("/distrbution-points?showAll=1");
        if (response.data.status !== 200) return;

        this.distributionPoints = filterActiveDistributionPoints(
          response.data.data
        );

        this.$nextTick(() => this.fitMapBounds());
      } catch (error) {
        console.error("Error fetching distribution points:", error);
      }
    },

    async fetchActiveDelegates() {
      try {
        const response = await this.$axios.get("drivers/active-delivering");
        if (response.data.status !== 200) return;

        const items = Array.isArray(response.data.data)
          ? response.data.data
          : [];

        this.delegates = items
          .map(mapActiveDeliveringToDelegate)
          .filter(Boolean);

        this.$nextTick(() => this.fitMapBounds());
      } catch (error) {
        console.error("Error fetching active delegates:", error);
      }
    },

    upsertDelegate(delegateData) {
      const existingIndex = this.delegates.findIndex(
        (d) => d.id === delegateData.id
      );

      if (existingIndex >= 0) {
        this.$set(this.delegates, existingIndex, {
          ...this.delegates[existingIndex],
          ...delegateData,
        });
        return;
      }

      this.delegates.push(delegateData);
    },

    initializeSocket() {
      this.socket = io(SOCKET_URL, {
        transports: ["websocket"],
        reconnection: true,
        reconnectionAttempts: 20,
        reconnectionDelay: 2000,
        timeout: 10000,
        forceNew: true,
      });

      this.socket.on("connect", () => {
        this.$iziToast.success({
          message: this.$t("PLACEHOLDERS.live_connection_established"),
        });
      });

      this.socket.on("location", this.updateDelegateMarker);
    },

    updateDelegateMarker(data) {
      const { user, lat, lng, bundle_id, timestamp } = data;
      if (!user?.id || lat == null || lng == null) return;

      const position = {
        lat: parseCoordinate(lat),
        lng: parseCoordinate(lng),
      };
      if (position.lat === null || position.lng === null) return;

      const delegateData = {
        id: user.id,
        name: user.name,
        avatar: user.picture,
        phone: user.phone,
        mobile: user.phone,
        lat: position.lat,
        lng: position.lng,
        bundle_id,
        last_update: timestamp,
        is_active: true,
      };

      this.upsertDelegate(delegateData);

      if (this.trackingDelegate?.id === user.id) {
        this.trackingDelegate = {
          ...this.trackingDelegate,
          ...delegateData,
        };
        this.getMapInstance()?.panTo(position);
      }
    },

    selectDelegate(delegate) {
      this.selectedType = "delegate";
      this.selectedDelegate = delegate;
      this.selectedDistributionPoint = null;
      this.showSidebar = true;
    },

    onDistributionGroupClick(group) {
      if (group.points.length === 1) {
        this.selectDistributionPoint(group.points[0]);
        return;
      }

      this.overlappingPoints = group.points;
      this.showPointPicker = true;
    },

    onPickDistributionPoint(point) {
      this.showPointPicker = false;
      this.selectDistributionPoint(point);
    },

    selectDistributionPoint(point) {
      this.selectedType = "distribution_point";
      this.selectedDistributionPoint = point;
      this.selectedDelegate = null;
      this.showSidebar = true;
    },

    closeSidebar() {
      this.showSidebar = false;
      this.selectedType = null;
      this.selectedDelegate = null;
      this.selectedDistributionPoint = null;
    },

    startTracking(delegate) {
      this.trackingDelegate = delegate;
      const map = this.getMapInstance();
      if (map) {
        map.setZoom(16);
        map.panTo({ lat: delegate.lat, lng: delegate.lng });
      }

      this.$iziToast.info({
        title: this.$t("PLACEHOLDERS.tracking_started"),
        message: `${this.$t("PLACEHOLDERS.now_tracking")} ${delegate.name}`,
      });
    },

    stopTracking() {
      this.trackingDelegate = null;
      this.fitMapBounds();
      this.$iziToast.info({
        title: this.$t("PLACEHOLDERS.tracking_stopped"),
        message: this.$t("PLACEHOLDERS.returned_to_overview"),
      });
    },

    navigateToBundle(bundleId) {
      if (bundleId) {
        this.$router.push(`/bundles/show/${bundleId}`);
      }
    },

    navigateToDelegate(delegateId) {
      if (delegateId) {
        this.$router.push(`/DeliveryMen/show/${delegateId}`);
      }
    },

    fitMapBounds() {
      fitMapToMarkers(
        this.getMapInstance(),
        this.delegates,
        this.distributionPoints
      );
    },

    cleanupSocket() {
      if (!this.socket) return;
      this.socket.off("location", this.updateDelegateMarker);
      this.socket.disconnect();
      this.socket = null;
    },
  },

  async mounted() {
    try {
      await Promise.all([
        this.fetchDistributionPoints(),
        this.fetchActiveDelegates(),
      ]);
      this.initializeSocket();
    } catch (error) {
      console.error("Error initializing delegates tracking:", error);
      this.$iziToast.error({
        title: this.$t("VALIDATION.error"),
        message: this.$t("VALIDATION.initialization_error"),
      });
    } finally {
      this.isLoading = false;
    }
  },

  beforeDestroy() {
    this.cleanupSocket();
  },

  watch: {
    showPointPicker(isOpen) {
      if (!isOpen) {
        this.overlappingPoints = [];
      }
    },
  },
};
</script>

<style lang="scss">
@import "./components/delegatesMap.scss";
</style>
